import React, { useState, useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import {
  Form,
  Input,
  Button,
  Radio,
  DatePicker,
  Select,
  NumberPicker,
  Notification,
  Checkbox,
  Balloon,
  TimePicker,
  Tooltip,
} from '@alifd/next';
import LzMsg from '@/components/common/LzMsg';
import styles from './index.module.scss';
import { CanvasService, Service } from '@/service';
import utils from '@/common/utils';
import { store } from '@ice/stark-data';

const RadioGroup = Radio.Group;
const formItemLayout = {
  labelCol: { fixedSpan: 6 },
};

const loopTypeList = [
  // 循环方式
  {
    value: 1,
    label: '单次执行',
  },
  {
    value: 2,
    label: '循环执行',
  },
];

const executionFrequency = [
  {
    value: 1,
    label: '按天',
  },
  {
    value: 2,
    label: '按周',
  },
  {
    value: 3,
    label: '按月',
  },
];

const weekList = [
  {
    value: 1,
    label: '周日',
  },
  {
    value: 2,
    label: '周一',
  },
  {
    value: 3,
    label: '周二',
  },
  {
    value: 4,
    label: '周三',
  },
  {
    value: 5,
    label: '周四',
  },
  {
    value: 6,
    label: '周五',
  },
  {
    value: 7,
    label: '周六',
  },
];

const timeList = [
  // 循环活动执行起始时间点
  {
    value: 2,
    label: '凌晨02:00',
  },
  {
    value: 14,
    label: '下午14:00',
  },
  {
    value: 1,
    label: '自定义时间',
  },
];

/**
 * dialogTitle 活动弹窗类型 1-新建 2-复制 3-修改
 */
const CreateActivity = ({
  canvasFormdata = {},
  submitFunc = {},
  cancelFunc = {},
  dialogTitle = 1,
}) => {
  // 用户订购软件剩余使用天数
  const [activityEndDate, setActivityEndDate] = useState(store.get('remainingDay'));

  // 弹窗内暂存表单数据
  const [formdata, setFormData] = useState({
    campaignName: '', // 活动名
    campaignRemark: '', // 备注
    executionPlan: {
      instancePackType: 1, // 固定传1
      type: 1, // 循环方式 1-单次 2-循环
      startTime: '', // 循环执行周期，需为时间戳
      endTime: '',
      frequencyType: 1, // 循环执行频率： 1-按天 2-按周 3-按月
      frequencyValue: 1, // 执行频率下的周期： 按天，按月就是字面量，按周等于∑2的n次幂
      startHour: 14, // 循环执行起始时间点 小时
      startMinute: 0, // 循环执行起始时间点 分
    },
  });
  const [disabledMinutes, setDisabledMinutes] = useState([]); // 循环--自定义事件不可点击的分钟
  const [customStartHour, setCustomStartHour] = useState(0); // 循环--选择自定义时间所选择的“时hour”
  const [radioCheck, setRadioCheck] = useState(0); // 循环--自定义时间单选的radio
  const [defaultHour, setDefaultHour] = useState(
    dialogTitle === 1
      ? '14'
      : JSON.parse(JSON.stringify(canvasFormdata)).executionPlan.startHour
  ); // 默认的时
  const [defaultMin, setDefaultMin] = useState(
    dialogTitle === 1
      ? '30'
      : JSON.parse(JSON.stringify(canvasFormdata)).executionPlan.startMinute
  ); // 默认的分
  // {moment(`${defaultHour}:${defaultMin}`, "HH:mm", true)}

  // 执行起始时间点的列表需要自己计算出来
  // const [hourList, setHourList] = useState([{
  //   label: '8',
  //   value: '8'
  // }]);

  /**
   * 循环执行选择时间
   */
  // 开始时间禁用
  const disabledStartDate = (date, view) => {
    const currentDate = new Date(new Date().setDate(new Date().getDate() - 1));
    return date.valueOf() < currentDate.valueOf();
  };
  // 结束时间禁用 与开始时间间隔90天
  const disabledEndDate = (date, view) => {
    const startVal = formdata.executionPlan.startTime; // 开始时间
    const startDate = moment(startVal).valueOf() + 89 * 24 * 60 * 60 * 1000; // 需和开始时间间隔90天
    const currentDate = new Date(new Date().setDate(new Date().getDate() - 1)); // 需大于当前日期
    const endDate = new Date(
      new Date().setDate(new Date().getDate() + activityEndDate)
    ).getTime(); // 软件过期时间
    return (
      date.valueOf() <= moment(startVal).valueOf() ||
      date.valueOf() < currentDate ||
      date.valueOf() > startDate ||
      date.valueOf() > endDate
    );
  };

  /**
   * 计算循环执行按天和按月执行的天数最大值
   */
  const calcDayMax = useMemo(() => {
    if (formdata.executionPlan.frequencyType !== 1) {
      return false;
    } else if (
      formdata.executionPlan.startTime &&
      formdata.executionPlan.endTime
    ) {
      const startDate = Date.parse(
        formdata.executionPlan.startTime.replace(/-/g, '/')
      );
      const endDate = Date.parse(
        formdata.executionPlan.endTime.replace(/-/g, '/')
      );
      const days = (endDate - startDate) / (1 * 24 * 60 * 60 * 1000);
      if (days < formdata.executionPlan.frequencyValue) {
        setFormData({
          ...formdata,
          executionPlan: {
            ...formdata.executionPlan,
            frequencyValue: Math.abs(days),
          },
        });
      }
      return Math.abs(days);
    } else {
      return formdata.executionPlan.frequencyValue;
    }
  }, [
    formdata.executionPlan.frequencyValue,
    formdata.executionPlan.frequencyType,
    formdata.executionPlan.startTime,
    formdata.executionPlan.endTime,
  ]);

  const calcMonthMax = (val) => {
    const monthVal = Number(val) ? Math.abs(Number(val)) : ''; // 原型中表示可以输入负数，但是目前这一版后端并不支持，所以暂时全部取整数
    // const date = new Date();
    // const year = date.getFullYear();
    // const month = date.getMonth()+1;
    // const d = new Date(year, month, 0);
    if (monthVal > 31) {
      setFormData({
        ...formdata,
        executionPlan: { ...formdata.executionPlan, frequencyValue: 31 },
      });
    } else {
      setFormData({
        ...formdata,
        executionPlan: { ...formdata.executionPlan, frequencyValue: monthVal },
      });
    }
  };

  /**
   * 循环执行选择执行频率
   */
  // const executionPeriod = () => {
  //   let hoursList = [...hourList];
  //   for(let i = 8; i < 24; i++) {
  //     hoursList.push({
  //       label: i + '',
  //       value: i + ''
  //     })
  //   }
  //   setHourList(hoursList);
  // };

  /**
   * 校验表单内容
   */
  const checkActivityName = (campaignName) => {
    return new Promise((resolve, reject) => {
      if (campaignName === '') {
        reject(['请输入活动名称']);
      } else {
        resolve();
      }
    });
  };
  const checkTime = (startTime) => {
    return new Promise((resolve, reject) => {
      if (
        startTime === '' ||
        formdata.executionPlan.endTime === '' ||
        new Date(formdata.executionPlan.startTime).getTime() >=
          new Date(formdata.executionPlan.endTime).getTime() ||
        new Date(`${formdata.executionPlan.endTime} 00:00:00`).getTime() <
          new Date(new Date().setHours(0, 0, 0, 0)).getTime()
      ) {
        reject(['请正确选择时间']);
      } else {
        resolve();
      }
    });
  };
  const validateFunc = (campaignName, startTime) => {
    return new Promise((resolve, reject) => {
      Promise.all([checkActivityName(campaignName), checkTime(startTime)])
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  };
  /**
   * 表单提交方法
   */
  const submitFormData = async () => {
    try {
      setFormData({
        ...formdata,
        executionPlan: {
          ...formdata.executionPlan,
          startHour: customStartHour,
        },
      });
      // 再次校验 如果单次循环和多次循环因参数不同，分别校验
      const dataForm = JSON.parse(JSON.stringify(formdata));
      dataForm.executionPlan.crowdStartTime = formdata.executionPlan.timeDifference === 0 ? 0 : formdata.executionPlan.crowdStartTime;
      dataForm.executionPlan.startTime = dataForm.executionPlan.startTime
        ? new Date(`${dataForm.executionPlan.startTime} 00:00:00`).getTime()
        : '';
      dataForm.executionPlan.endTime = dataForm.executionPlan.endTime
        ? new Date(`${dataForm.executionPlan.endTime} 23:59:59`).getTime()
        : '';

      if (
        (dataForm.executionPlan.endTime - dataForm.executionPlan.startTime) /
          (1000 * 60 * 60 * 24) >
        activityEndDate
      ) {
        LzMsg.error('执行周期已超过软件剩余可用天数，请重新设置');
        return;
      }

      if (dataForm.executionPlan.type === 1) {
        // 单次活动
        await checkActivityName(dataForm.campaignName);
        submitFunc(dataForm);
      } else {
        // 循环活动
        // return;
        // 判断执行频率不能为空
        if (
          !dataForm.executionPlan.frequencyValue &&
          dataForm.executionPlan.frequencyValue !== 0
        ) {
          LzMsg.error('执行频率不能为空');
          return;
        } else if (
          dataForm.executionPlan.frequencyValue instanceof Array &&
          dataForm.executionPlan.frequencyValue.length === 0
        ) {
          LzMsg.error('执行频率不能为空');
          return;
        } else if (
          String(dataForm.executionPlan.frequencyValue).indexOf('.') >= 0
        ) {
          LzMsg.error('执行频率的天数只能是整数');
          return;
        }
        let frequencyValues = 0;
        if (dataForm.executionPlan.frequencyType === 2) {
          // 按周执行
          dataForm.executionPlan.frequencyValue =
            dataForm.executionPlan.frequencyValue === 1
              ? [1] : dataForm.executionPlan.frequencyValue;
          for (
            let tip = 0;
            tip < dataForm.executionPlan.frequencyValue.length;
            tip++
          ) {
            frequencyValues += Math.pow(
              2,
              dataForm.executionPlan.frequencyValue[tip]
            );
          }
          dataForm.executionPlan.frequencyValue = frequencyValues;

          // 判断在所选时间范围内是不是有有效的循环周期
          const dataTypeStarttime = new Date(formdata.executionPlan.startTime); // 活动开始时间
          const dataTypeEndtime = new Date(formdata.executionPlan.endTime); // 活动结束时间
          const stDay = dataTypeStarttime.getDay(); // 开始结束星期
          const enDay = dataTypeEndtime.getDay();
          const stTime = dataTypeStarttime.getTime(); // 开始结束日期
          const edTime = dataTypeEndtime.getTime();
          // 判断如果周期小于7天，就看我所选的星期是否在我的周期内
          if ((edTime - stTime) / (1000 * 60 * 60 * 24) < 7) {
            const val = formdata.executionPlan.frequencyValue === 1
                  ? [1] : formdata.executionPlan.frequencyValue; // 由于dataForm里已经是计算完准备传给后端的值了，这里还是得用formdata的值看在没在范围内，处理成数组就是因为如果只选了周日，就是1，不能some
            if (!val.some(
                (item) =>
                (+item >= stDay + 1 && +item <= enDay + 1) ||
                (+item <= stDay + 1 && +item <= enDay + 1 && (stDay > enDay))
            )) {
                LzMsg.error('执行频率需要在有效的执行周期内');
                return;
            }
          }
        }

        if (dataForm.executionPlan.frequencyType === 3) {
          // 按月执行-执行频率是否在有效执行周期内
          const stDay = new Date(formdata.executionPlan.startTime).getDate();
          const enDay = new Date(formdata.executionPlan.endTime).getDate();
          if (
            !(
              formdata.executionPlan.frequencyValue >= stDay + 1 &&
              formdata.executionPlan.frequencyValue <= enDay + 1
            )
          ) {
            LzMsg.error('执行频率需要在有效的执行周期内');
            return;
          }
        }
        await validateFunc(
          dataForm.campaignName,
          dataForm.executionPlan.startTime
        );
        submitFunc(dataForm);
      }
    } catch (e) {
      LzMsg.error(e[0]);
    }
  };
  const cancelDialog = () => {
    try {
      // 恢复表单内容为父组件传的值（PS: 这个会导致useMemo监听执行频率的两个方法再次执行，所以由于并不影响使用，可以不执行这个方法）
      // setFormData(canvasFormdata);
      cancelFunc();
    } catch (e) {
      Notification.open({
        title: '失败',
        content: e,
        icon: 'delete-filling',
      });
    }
  };

  /**
   * 计算出循环执行时的执行起始时间点小时列表，初始化父组件传过来的值
   */
  useEffect(() => {
    const formDatas = JSON.parse(JSON.stringify(canvasFormdata));
    formDatas.executionPlan.timeDifference = formDatas.executionPlan.timeDifference === undefined ? 0 : formDatas.executionPlan.timeDifference;
    formDatas.executionPlan.crowdStartTime = formDatas.executionPlan.crowdStartTime
      ? moment(formDatas.executionPlan.crowdStartTime).format('YYYY-MM-DD HH:mm:ss')
      : moment(new Date().getTime()).format('YYYY-MM-DD HH:mm:ss');
    formDatas.executionPlan.startTime = formDatas.executionPlan.startTime
      ? moment(formDatas.executionPlan.startTime).format('YYYY-MM-DD')
      : '';
    formDatas.executionPlan.endTime = formDatas.executionPlan.endTime
      ? moment(formDatas.executionPlan.endTime).format('YYYY-MM-DD')
      : '';
    if (formDatas.executionPlan.frequencyType === 2) {
      const x = formDatas.executionPlan.frequencyValue;
      const binaryStringValue = x.toString(2);
      const result = [];
      const length = binaryStringValue.length - 1;
      for (let i = length; i >= 0; i--) {
        const c = binaryStringValue.charAt(i);
        if (c == '1') {
          result.push(length - i);
        }
      }
      formDatas.executionPlan.frequencyValue = result;
    }
    if (dialogTitle === 2) {
      // 如果是复制活动，在活动名后添加‘副本’
      formDatas.campaignName += '副本';
      if (formDatas.campaignName.length >= 80) {
        formDatas.campaignName = '复制活动副本';
      }
    }
    if (
      formDatas.executionPlan.startHour === 2 ||
      (formDatas.executionPlan.startHour === 14 &&
        formDatas.executionPlan.startMinute === 0)
    ) {
      setRadioCheck(formDatas.executionPlan.startHour);
    } else {
      setRadioCheck(1);
    }
    setFormData({ ...formDatas });
    console.log({ ...formDatas }, '=====formDatas===');
  }, []);

  const disabledItems = (list) => (index) => {
    return list.indexOf(index) >= 0;
  };
  /**
   * 针对单次执行的活动，时间选择器自定义页脚，增加‘本日’按钮，添加点击按钮收起面板方法
   */
   const [endOpen, setEndOpen] = useState(false);
   const handleEndOpenChange = (open) =>{
     setEndOpen(open);
   };
  const disabledHours = [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 19, 20, 21, 22, 23,
    ]; // 自定义时间：限制下午14：30到晚上18:59范围内

    const defaultVal = useMemo(
        () => defaultMin > 10
        ? moment(`${defaultHour}:${defaultMin}`, 'HH:mm', true)
        : moment(`${defaultHour}:0${defaultMin}`, 'HH:mm', true),
        [defaultHour, defaultMin]
    );

  return (
    <div>
    <div style={{ marginTop: '30px'}}>
      <Form
        {...formItemLayout}
        className={`${styles['create-activity-dialog-root']} ${styles.loopRadio}`}
      >
        <Form.Item label="活动名称：" required requiredMessage="请输入活动名称" labelAlign='top'>
          <Input
            name="createactivityName"
            placeholder="请输入活动名称"
            size="small"
            trim
            className={`formItemWidth ${
              dialogTitle !== 2 ? styles['active-name'] : null
            }`}
            value={formdata.campaignName}
            onChange={(val) => setFormData({ ...formdata, campaignName: val })}
            showLimitHint
            maxLength={80}
          />
        </Form.Item>
        {/* <Form.Item label="循环方式：" required requiredMessage="请选择循环方式" labelAlign='top'>
          <Radio.Group
            disabled={dialogTitle !== 1}
            className="formItemWidth"
            dataSource={loopTypeList}
            value={formdata.executionPlan.type}
            onChange={(val) =>
              setFormData({
                ...formdata,
                executionPlan: { ...formdata.executionPlan, type: Number(val) },
                campaignRemark: '',
              })
            }
          />

        </Form.Item> */}
        {formdata.executionPlan.type === 1 && ( //循环方式--单次执行
          <Form.Item label="执行时间：（画布里人群各节点开始运算的时间，非短信发送时间）" required requiredMessage="请选择执行时间" labelAlign='top'>
            {/* <span>画布开始执行时间（人群运算开始时间）</span>
            <Balloon.Tooltip
                align="t"
                trigger={
                  <span style={{fontSize:"14px",cursor:"pointer", verticalAlign:'middle'}} className={`iconfont icon-icon4-33`}>：</span>
                }
                closable={false}
              >
                <div style={{ lineHeight: '20px' }}>
                  画布里人群各节点开始运算的时间，非短信发送时间
                </div>
              </Balloon.Tooltip> */}
            <RadioGroup
              value={formdata.executionPlan.timeDifference ? formdata.executionPlan.timeDifference :0}
              onChange={(val) =>
                setFormData({
                    ...formdata,
                    executionPlan: {
                    ...formdata.executionPlan,
                    timeDifference: val,
                    },
                })
              }
              aria-labelledby="groupId"
            >
            <Radio id="0" value={0}>
              立即执行
              <Balloon.Tooltip
                align="t"
                trigger={
                  <span style={{fontSize:"14px",marginLeft:"4px",cursor:"pointer"}} className={`iconfont icon-icon4-33`}></span>
                }
                closable={false}
              >
                <div style={{ lineHeight: '20px' }}>
                  提交后立即执行画布任务
                </div>
              </Balloon.Tooltip>
            </Radio>
            <Radio id="1" value={1}>
              指定时间
              <Balloon.Tooltip
                align="t"
                trigger={
                  <span style={{fontSize:"14px",marginLeft:"4px",cursor:"pointer"}} className={`iconfont icon-icon4-33`}></span>
                }
                closable={false}
              >
                <div style={{ lineHeight: '20px' }}>
                  提交成功后，在指定时间开始执行画布任务（若提交时间晚于指定时间，即提交后立即执行）
                </div>
              </Balloon.Tooltip>
            </Radio>
            </RadioGroup>
            { formdata.executionPlan.timeDifference === 1 &&
            <DatePicker className="formItemWidth"
              style={{marginTop: '10px'}}
              hasClear={false}
              showTime
              value={formdata.executionPlan.crowdStartTime}
              // disabledHours={disabledItems([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 20, 21, 22, 23])}
              onChange={(val) => {
                  setFormData({
                  ...formdata,
                  executionPlan: {
                      ...formdata.executionPlan,
                      crowdStartTime: moment(val).format('YYYY-MM-DD HH:mm:ss'),
                  },
                  })
                }}
              onVisibleChange={handleEndOpenChange}
            />
            }
          </Form.Item>
        )}
        {formdata.executionPlan.type === 2 && ( //循环方式--循环执行
          <div className={styles.remarksDiv}>
            <span className={`${styles.trigel} ${styles.trigel1}`}><span className={styles.top}></span></span>
                  <Form.Item
                    label="执行周期:"
                    required
                    requiredMessage="请选择执行周期"
                  >
                    <div>
                      <DatePicker
                        style={{width: '48%'}}
                        disabledDate={disabledStartDate}
                        value={formdata.executionPlan.startTime}
                        placeholder="起始时间"
                        onChange={(val) =>
                          setFormData({
                            ...formdata,
                            executionPlan: {
                              ...formdata.executionPlan,
                              startTime: val
                                ? moment(val).format('YYYY-MM-DD')
                                : 0,
                            },
                          })
                        }
                      />
                      <span className="custom-sep">  -  </span>
                      <DatePicker
                        style={{width: '48%'}}
                        disabledDate={disabledEndDate}
                        value={formdata.executionPlan.endTime}
                        placeholder="截止时间"
                        onChange={(val) =>
                          setFormData({
                            ...formdata,
                            executionPlan: {
                              ...formdata.executionPlan,
                              endTime: val
                                ? moment(val).format('YYYY-MM-DD')
                                : 0,
                            },
                          })
                        }
                      />
                      <div className={styles.notes}>
                        注：执行周期最多不可超过90天
                      </div>
                    </div>
                  </Form.Item>
                  <Form.Item
                    label="执行频率:"
                    required
                    requiredMessage="请选择执行频率"
                  >
                    <div className={`${styles.displayFlex} formItemWidth`}>
                      <Select
                        followTrigger
                        value={formdata.executionPlan.frequencyType}
                        onChange={(val) =>
                          setFormData({
                            ...formdata,
                            executionPlan: {
                              ...formdata.executionPlan,
                              frequencyType: val,
                              frequencyValue: 1,
                            },
                          })
                        }
                        placeholder="请选择执行频率"
                      >
                        {executionFrequency.map((i, index) => (
                          <Select.Option
                            value={i.value}
                            key={`frequency${i.label}`}
                          >
                            {i.label}
                          </Select.Option>
                        ))}
                      </Select>
                      {formdata.executionPlan.frequencyType === 1 && (
                        <div style={{ marginLeft: '5px' }}>
                          <span> 每 </span>{' '}
                          <NumberPicker
                            type="inline"
                            editable={false}
                            value={formdata.executionPlan.frequencyValue}
                            min={1}
                            max={calcDayMax}
                            onChange={(val) =>
                              setFormData({
                                ...formdata,
                                executionPlan: {
                                  ...formdata.executionPlan,
                                  frequencyValue: val,
                                },
                              })
                            }
                          />{' '}
                          <span> 日，执行一次</span>
                        </div>
                      )}
                      {formdata.executionPlan.frequencyType === 3 && (
                        <div style={{ marginLeft: '5px' }}>
                          <Input
                            name="monthValue"
                            trim
                            className="monthTypeActivyInput"
                            value={formdata.executionPlan.frequencyValue}
                            onChange={(val) => calcMonthMax(val)}
                          />
                          {/* <NumberPicker min={1} max={calcMonthMax} onChange={(val) => setFormData({ ...formdata, executionPlan: {...formdata.executionPlan, frequencyValue: val}})} value={formdata.executionPlan.frequencyValue}/> */}
                          <span style={{ marginLeft: '5px' }}>日</span>
                          {/* <div className={`${styles.notes} ${styles['month-type-input']}`}>注：需填写整数，如填写负数，则表示从每月最后一天开始计算</div> */}
                        </div>
                      )}
                    </div>
                    {formdata.executionPlan.frequencyType === 2 && (
                      <Checkbox.Group
                        direction="hoz"
                        value={formdata.executionPlan.frequencyValue}
                        onChange={(val) =>
                          setFormData({
                            ...formdata,
                            executionPlan: {
                              ...formdata.executionPlan,
                              frequencyValue: val,
                            },
                          })
                        }
                      >
                        {weekList.map((i, index) => (
                          <Checkbox value={i.value} key={i.value}>
                            {i.label}
                          </Checkbox>
                        ))}
                      </Checkbox.Group>
                    )}
                    <div className={styles.notes}>
                      注：同一用户两天内只能被短信触达一次
                    </div>
                  </Form.Item>
                  <Form.Item label="执行起始时间点:" required>
                    <div>
                      <Radio.Group
                        dataSource={timeList}
                        value={radioCheck || 14}
                        onChange={(val) => {
                          setRadioCheck(val);
                          setCustomStartHour(val);
                          if (val == 2 || val === 14) {
                            setCustomStartHour(val);
                            setFormData({
                              ...formdata,
                              executionPlan: {
                                ...formdata.executionPlan,
                                startHour: val,
                                startMinute: 0,
                              },
                            });
                          } else {
                            setDefaultHour(14); // 由于自定义时间，14之前都不可选，这里默认让每次点击自定义时间都用14开始
                            setDefaultMin(30);
                            setFormData({
                              ...formdata,
                              executionPlan: {
                                ...formdata.executionPlan,
                                startMinute: 30,
                              },
                            });
                          }
                        }}
                      />
                      {radioCheck === 1 && (
                        <div style={{ margin: '4px 0' }}>
                          <TimePicker
                            hasClear={false}
                            placeholder="请选择自定义时间"
                            disabledHours={disabledItems(disabledHours)}
                            disabledMinutes={disabledItems(disabledMinutes)}
                            format="H:mm"
                            style={{ width: '70%' }}
                            onChange={(v) => {
                              // 14:30以后才能点击 6:59之前才能点击
                              if (v._d.getHours() === 14) {
                                setDisabledMinutes([
                                  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13,
                                  14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
                                  25, 26, 27, 28, 29,
                                ]);
                              } else {
                                setDisabledMinutes([]);
                              }
                              setCustomStartHour(v._d.getHours() ?? 0);

                              setDefaultHour(v._d.getHours() ?? 0);
                              setDefaultMin(v._d.getMinutes() ?? 0);
                              setFormData({
                                ...formdata,
                                executionPlan: {
                                  ...formdata.executionPlan,
                                  startHour: v._d.getHours() ?? 0,
                                  startMinute: v._d.getMinutes() ?? 0,
                                },
                              });
                            }}
                            value={defaultVal}
                          />
                        </div>
                      )}
                      <div
                        style={{
                          color: '#9ca7b6',
                          marginTop: '5px',
                          lineHeight: '20px',
                        }}
                      >
                        注：因受官方数据推送时间限制，不同人群需求请选择不同的执行起始时间。
                        <br />
                        <Balloon.Tooltip
                          align="t"
                          trigger={
                            <span
                              className={styles.cursorPointer}
                              style={{ color: '#3399ff' }}
                            >
                              起始时间的规则及内容
                            </span>
                          }
                          closable={false}
                        >
                          <div style={{ lineHeight: '20px' }}>
                            <span>
                              1.选择凌晨2:00时，无法获取人群标签T+1的人群数据（即无法获取策略中涉及昨天的人群数据），但短信发送时间可以在频控时间内任意选择。
                            </span>
                            <br />
                            <span>
                              2.选择下午14:00及之后时，可以获取到T+1数据，但是短信发送时间需要设置到执行时间之后，并因前直节点需要执行时间，推荐短信发送节点时间设置在16:00之后。
                            </span>
                          </div>
                        </Balloon.Tooltip>
                      </div>
                    </div>
                  </Form.Item>
                </div>
        )}
      </Form>
      <Form {...formItemLayout}
        className={`${styles['create-activity-dialog-root']} ${styles.loopRadio}`}>
        <Form.Item label="备注:" className="customActivityRemark" labelAlign='top'>
          <Input.TextArea
            className={`${styles.textAreaWidth}`}
            name="campaignRemark"
            placeholder="请输入备注"
            size="small"
            value={formdata.campaignRemark}
            onChange={(val) =>
              setFormData({ ...formdata, campaignRemark: val })
            }
            showLimitHint
            maxLength={150}
          />
        </Form.Item>
      </Form>
    </div>
    <hr />
    <div>
      <Button
        className={`${styles.w88} ${styles.mr30} ${styles.mt15}`}
        type="primary"
        style={{ float: 'right',marginBottom:'15px' }}
        onClick={submitFormData}
      >
        确认
      </Button>
      <Button
        type="normal"
        className={`${styles.mr10} ${styles.w88} ${styles.mt15}`}
        style={{ float: 'right',marginBottom:'15px' }}
        onClick={cancelDialog}
      >
        取消
    </Button>
    </div>
    </div>
  );
};
export default CreateActivity;
